import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Star, MapPin } from "lucide-react";
import { scrollToId } from "./NavHeader";

const HERO_IMG = "/hero-office-flipped.webp";

const LINES = [
  { text: "Big-picture accounting,", italic: false },
  { text: "small-firm heart.", italic: true },
];

export const HeroKinetic = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={HERO_IMG}
          alt="Luke and Abbie working at their office in Bristol & Bath Science Park"
          className="w-full h-full object-cover"
          style={{ objectPosition: "50% 42%" }}
        />
        <div className="absolute inset-0 bg-[#FDFBF7]/[0.85]" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#FDFBF7] to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#103080]/15 bg-white/60 backdrop-blur-md px-4 py-2"
          data-testid="hero-location-badge"
        >
          <MapPin size={14} className="text-[#0090D0]" />
          <span className="text-xs sm:text-sm font-medium tracking-wide text-[#103080]">
            Small business accountants · Bristol &amp; Bath Science Park
          </span>
        </motion.div>

        <h1 className="font-serif font-semibold tracking-tight text-[#103080] text-5xl sm:text-6xl lg:text-8xl leading-[1.02]">
          {LINES.map((line, i) => (
            <span key={line.text} className="mask-line pb-1">
              <motion.span
                className={`block ${line.italic ? "italic text-[#0090D0]" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.35 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-base sm:text-lg text-[#0F172A]/75 leading-relaxed"
          data-testid="hero-subcopy"
        >
          Rosevale is Luke &amp; Abbie — a husband-and-wife team helping and advising
          Bristol's businesses, limited companies, landlords and the self-employed
          keep more of what they earn. Fixed fees. A free discovery call to start.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="https://calendly.com/abbie-browne"
            target="_blank"
            rel="noreferrer"
            data-testid="hero-book-call-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#103080] text-white font-medium px-8 py-4 text-sm sm:text-base hover:bg-[#0090D0] transition-colors duration-300"
          >
            Book a free discovery call
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
          <a
            href="#services"
            data-testid="hero-services-link"
            onClick={(e) => { e.preventDefault(); scrollToId("#services"); }}
            className="inline-flex items-center gap-2 rounded-full border border-[#103080]/25 text-[#103080] font-medium px-8 py-4 text-sm sm:text-base hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-300 bg-white/50 backdrop-blur-sm"
          >
            Explore our services
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-10 flex items-center gap-3"
          data-testid="hero-google-rating"
        >
          <span className="flex gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={16} className="fill-amber-400 text-amber-400" />
            ))}
          </span>
          <span className="text-sm text-[#0F172A]/70 font-medium">5.0 · 12 reviews on Google</span>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-8 hidden md:flex z-10 w-12 h-12 rounded-full border border-[#103080]/20 items-center justify-center text-[#103080] bg-white/50 backdrop-blur-sm"
        aria-label="Scroll to services"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={18} />
        </motion.span>
      </motion.button>
    </section>
  );
};
